<template>
  <div ref="root" class="relative">
    <!-- Bell trigger -->
    <button
      type="button"
      class="relative flex items-center justify-center rounded-full p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
      :aria-label="`Powiadomienia${unreadCount > 0 ? `, nieprzeczytane: ${unreadCount}` : ''}`"
      :aria-expanded="open"
      @click="toggle"
    >
      <Icon
        name="bell"
        class="h-6 w-6 stroke-current"
        :class="{ 'bell-swing': animating }"
      />
      <!-- Unread badge -->
      <span
        v-if="unreadCount > 0"
        class="absolute -right-0.5 -top-0.5 flex min-h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold leading-none text-white"
      >
        {{ badgeLabel }}
        <span
          v-if="animating"
          class="absolute inset-0 rounded-full bg-red-500 opacity-75 motion-safe:animate-ping"
        />
      </span>
    </button>

    <!-- Dropdown -->
    <transition
      enter-active-class="transition ease-out duration-150"
      enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition ease-in duration-100"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-1"
    >
      <div
        v-if="open"
        class="absolute right-0 z-50 mt-2 w-80 origin-top-right overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl sm:w-96"
      >
        <!-- Header -->
        <div class="flex items-center justify-between border-b border-gray-100 px-4 py-3">
          <h3 class="text-sm font-semibold text-gray-900">
            Powiadomienia
          </h3>
          <button
            v-if="unreadCount > 0"
            type="button"
            class="text-xs font-medium text-indigo-600 hover:text-indigo-800"
            @click="markAllRead"
          >
            Oznacz wszystkie jako przeczytane
          </button>
        </div>

        <!-- List -->
        <ul
          v-if="items.length"
          class="max-h-96 divide-y divide-gray-100 overflow-y-auto"
        >
          <li
            v-for="item in items"
            :key="item.id"
            class="flex cursor-pointer gap-3 px-4 py-3 transition-colors hover:bg-gray-50"
            :class="{ 'bg-indigo-50/60': !item.read }"
            @click="onItemClick(item)"
          >
            <span
              class="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full"
              :class="iconWrapClass(item.kind)"
            >
              <Icon :name="iconName(item.kind)" class="h-4 w-4 stroke-current" />
            </span>
            <div class="min-w-0 flex-1">
              <p class="truncate text-sm font-medium text-gray-900">
                {{ item.title }}
              </p>
              <p v-if="item.body" class="mt-0.5 line-clamp-2 text-xs text-gray-500">
                {{ item.body }}
              </p>
              <p class="mt-1 text-[11px] text-gray-400">
                {{ relativeTime(item.created_at) }}
              </p>
            </div>
            <span
              v-if="!item.read"
              class="mt-1 h-2 w-2 shrink-0 rounded-full bg-indigo-500"
              aria-label="Nieprzeczytane"
            />
          </li>
        </ul>

        <!-- Empty state -->
        <div v-else class="px-4 py-10 text-center">
          <Icon name="bell" class="mx-auto h-8 w-8 stroke-current text-gray-300" />
          <p class="mt-2 text-sm text-gray-500">
            Brak powiadomień
          </p>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import { router } from '@inertiajs/vue3';
import Icon from '@/Shared/Icon.vue';
import { subscribeToAdminNotifications } from '@/utils/adminNotificationsCable';

export default {
  components: { Icon },
  data() {
    const shared = this.$page.props.notifications || {};
    return {
      open: false,
      animating: false,
      items: Array.isArray(shared.recent) ? shared.recent.slice() : [],
      unreadCount: Number(shared.unread_count) || 0,
      unsubscribe: null,
      animationTimer: null,
    };
  },
  computed: {
    badgeLabel() {
      return this.unreadCount > 99 ? '99+' : String(this.unreadCount);
    },
  },
  mounted() {
    this.unsubscribe = subscribeToAdminNotifications((data) => this.onIncoming(data));
    document.addEventListener('click', this.onDocumentClick);
  },
  beforeUnmount() {
    if (this.unsubscribe) this.unsubscribe();
    if (this.animationTimer) clearTimeout(this.animationTimer);
    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    csrfToken() {
      return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    },
    toggle() {
      this.open = !this.open;
      // Opening acknowledges the "new" pulse but keeps items unread until read.
      if (this.open) this.stopAnimation();
    },
    close() {
      this.open = false;
    },
    onDocumentClick(event) {
      if (this.open && this.$refs.root && !this.$refs.root.contains(event.target)) {
        this.close();
      }
    },
    onIncoming(data) {
      const incoming = data.notification;
      if (incoming && !this.items.some((item) => item.id === incoming.id)) {
        this.items.unshift(incoming);
        this.items = this.items.slice(0, 15);
      }
      this.unreadCount = Number(data.unread_count) || this.unreadCount;
      this.triggerAnimation();
    },
    triggerAnimation() {
      this.animating = true;
      if (this.animationTimer) clearTimeout(this.animationTimer);
      this.animationTimer = setTimeout(() => {
        this.animating = false;
      }, 1500);
    },
    stopAnimation() {
      this.animating = false;
      if (this.animationTimer) clearTimeout(this.animationTimer);
    },
    onItemClick(item) {
      if (!item.read) this.markRead(item);
      if (item.url) {
        this.close();
        router.visit(item.url);
      }
    },
    markRead(item) {
      const wasUnread = !item.read;
      item.read = true;
      if (wasUnread) this.unreadCount = Math.max(0, this.unreadCount - 1);

      axios
        .post(
          this.$routes.mark_read_admin_notification(item.id),
          {},
          { headers: { 'X-CSRF-Token': this.csrfToken(), Accept: 'application/json' } },
        )
        .then((response) => {
          if (response.data && typeof response.data.unread_count === 'number') {
            this.unreadCount = response.data.unread_count;
          }
        })
        .catch(() => {
          // Revert optimistic state on failure.
          item.read = false;
          if (wasUnread) this.unreadCount += 1;
        });
    },
    markAllRead() {
      this.items = this.items.map((item) => ({ ...item, read: true }));
      this.unreadCount = 0;

      axios
        .post(
          this.$routes.mark_all_read_admin_notifications(),
          {},
          { headers: { 'X-CSRF-Token': this.csrfToken(), Accept: 'application/json' } },
        )
        .catch(() => {});
    },
    iconName(kind) {
      if (kind === 'contact_request') return 'user';
      if (kind === 'booking_interest') return 'question-mark';
      return 'calendar-days';
    },
    iconWrapClass(kind) {
      if (kind === 'contact_request') return 'bg-amber-100 text-amber-600';
      if (kind === 'booking_interest') return 'bg-emerald-100 text-emerald-600';
      return 'bg-indigo-100 text-indigo-600';
    },
    relativeTime(iso) {
      if (!iso) return '';
      const then = new Date(iso);
      const seconds = Math.round((Date.now() - then.getTime()) / 1000);
      if (seconds < 60) return 'przed chwilą';
      const minutes = Math.round(seconds / 60);
      if (minutes < 60) return `${minutes} min temu`;
      const hours = Math.round(minutes / 60);
      if (hours < 24) return `${hours} godz. temu`;
      const days = Math.round(hours / 24);
      if (days < 7) return `${days} ${days === 1 ? 'dzień' : 'dni'} temu`;
      return then.toLocaleDateString('pl-PL');
    },
  },
};
</script>
